// src/utils/ErrorHandler.js

/**
 * A utility class for handling and formatting API error responses into user-friendly messages.
 * This class processes various types of errors including:
 * - HTTP status errors (400, 401, 403, etc.)
 * - Database integrity errors (unique constraints, foreign keys)
 * - Validation errors
 * - Network errors
 */
class ErrorHandler {
    /**
     * Takes an error object from an API response and formats it into a user-friendly message.
     * The method analyzes different parts of the error object to determine the most appropriate
     * message to display to the user.
     * 
     * @param {Error} error - The error object from a caught exception
     * @returns {string} A formatted, user-friendly error message
     * 
     * @example
     * try {
     *   await api.saveData(data);
     * } catch (error) {
     *   const message = ErrorHandler.formatErrorMessage(error);
     *   showAlert(message);
     * }
     */
    static formatErrorMessage(error) {
        // If there's no response, it's likely a network error
        if (!error.response) {
            return this.handleNetworkError(error);
        }

        const { status, data } = error.response;

        // Handle cases where there's no detailed error data
        if (!data) {
            return this.getStatusBasedMessage(status);
        }

        // Extract the error details from the response
        const errorDetail = data.detail;
        if (!errorDetail) {
            return this.getStatusBasedMessage(status);
        }

        // Process the error detail based on its type and content
        return this.processErrorDetail(errorDetail, status);
    }

    /**
     * Handles network-related errors that occur before reaching the server
     * @private
     */
    static handleNetworkError(error) {
        if (error.message === 'Network Error') {
            return 'Unable to connect to the server. Please check your internet connection.';
        }
        if (error.message.includes('timeout')) {
            return 'The request timed out. Please try again.';
        }
        return error.message || 'A network error occurred. Please check your connection and try again.';
    }

    /**
     * Processes the error detail and determines the appropriate formatting method
     * @private
     */
    static processErrorDetail(errorDetail, status) {
        if (typeof errorDetail === 'string') {
            // Check for specific error types
            if (errorDetail.includes('Integrity error')) {
                return this.parseIntegrityError(errorDetail);
            }
            if (errorDetail.includes('Validation error')) {
                return this.parseValidationError(errorDetail);
            }
            if (errorDetail.includes('Foreign key violation')) {
                return this.parseForeignKeyError(errorDetail);
            }
            if (errorDetail.includes('not found')) {
                return this.parseNotFoundError(errorDetail);
            }
            
            // For unknown patterns, clean up the message
            return this.cleanErrorMessage(errorDetail);
        }

        // Handle array of FastAPI validation errors
        if (Array.isArray(errorDetail)) {
            return this.parseValidationErrorArray(errorDetail);
        }

        // Handle structured error objects
        if (typeof errorDetail === 'object') {
            return Object.entries(errorDetail)
                .map(([key, value]) => `${this.formatFieldName(key)}: ${value}`)
                .join('\n');
        }

        // Fallback to status-based message
        return this.getStatusBasedMessage(status);
    }

    /**
     * Parses database integrity errors into user-friendly messages
     * @private
     */
    static parseIntegrityError(errorDetail) {
        const detailMatch = errorDetail.match(/DETAIL:\s*(.*?)(?=\s*\[|$)/);
        if (!detailMatch) {
            return 'This record cannot be saved due to a conflict with existing data.';
        }

        const detail = detailMatch[1].trim();

        // Handle unique constraint violations
        if (detail.includes('Key (')) {
            const keyMatch = detail.match(/Key \((.*?)\)=\((.*?)\)/);
            if (keyMatch) {
                const [, field, value] = keyMatch;
                return this.formatDuplicateKeyMessage(field, value);
            }
        }

        return this.cleanErrorMessage(detail);
    }

    /**
     * Maps field names to user-friendly error messages for duplicate key violations
     * @private
     */
    static formatDuplicateKeyMessage(field, value) {
        // Map of field names to custom error messages
        const fieldMessages = {
            'entry_number': `Entry number "${value}" is already in use. Please use a different entry number.`,
            'manifest_reference': `Manifest reference "${value}" is already in use.`,
            'tracking_number': `Tracking number "${value}" is already in use.`,
            'container_number': `Container number "${value}" is already in use.`,
            'invoice_number': `Invoice number "${value}" is already in use.`,
            'email': `The email address "${value}" is already registered.`,
            'username': `The username "${value}" is already taken.`
        };

        // Return custom message if available, otherwise use a generic format
        return fieldMessages[field] || 
               `The ${this.formatFieldName(field)} "${value}" is already in use.`;
    }

    /**
     * Returns appropriate error messages based on HTTP status codes
     * @private
     */
    static getStatusBasedMessage(status) {
        const statusMessages = {
            400: "Invalid request. Please check your input and try again.",
            401: "Your session has expired. Please login again.",
            403: "You don't have permission to perform this action.",
            404: "The requested resource was not found.",
            409: "This operation could not be completed due to a conflict.",
            422: "The submitted data is invalid.",
            429: "Too many requests. Please try again later.",
            500: "An internal server error occurred. Please try again later.",
            502: "The server is temporarily unavailable. Please try again later.",
            503: "Service unavailable. Please try again later."
        };

        return statusMessages[status] || "An unexpected error occurred. Please try again.";
    }

    /**
     * Formats field names into user-readable text
     * @private
     */
    static formatFieldName(fieldName) {
        return fieldName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Parses an array of FastAPI validation errors into user-friendly messages
     * @param {Array} errors - Array of validation error objects
     * @returns {string} Formatted error message
     * @private
     */
    static parseValidationErrorArray(errors) {
        return errors.map(error => {
            // Get the field name from the location array
            const fieldPath = error.loc
                .slice(1)  // Remove 'body' from the start
                .map(part => {
                    // If it's a number, it's an array index
                    if (typeof part === 'number') {
                        return `item ${part + 1}`;
                    }
                    return this.formatFieldName(part);
                })
                .join(' ');

            // Handle specific error types
            switch (error.type) {
                case 'float_parsing':
                case 'int_parsing':
                    return `Invalid number format in ${fieldPath}: "${error.input}"`;
                case 'value_error.missing':
                    return `${fieldPath} is required`;
                case 'value_error.number.not_gt':
                    return `${fieldPath} must be greater than ${error.ctx?.limit}`;
                case 'value_error.number.not_ge':
                    return `${fieldPath} must be greater than or equal to ${error.ctx?.limit}`;
                case 'value_error.number.not_lt':
                    return `${fieldPath} must be less than ${error.ctx?.limit}`;
                case 'value_error.number.not_le':
                    return `${fieldPath} must be less than or equal to ${error.ctx?.limit}`;
                case 'value_error.any_str.min_length':
                    return `${fieldPath} must be at least ${error.ctx?.limit} characters long`;
                case 'value_error.any_str.max_length':
                    return `${fieldPath} cannot be longer than ${error.ctx?.limit} characters`;
                default:
                    // For unknown error types, use the provided message
                    return `${fieldPath}: ${error.msg}`;
            }
        }).join('\n');
    }

    /**
     * Cleans and standardizes error messages
     * @private
     */
    static cleanErrorMessage(message) {
        if (typeof message !== 'string') {
            return 'An error occurred';
        }
        
        return message
            .replace(/^\[|\]$/g, '')           // Remove brackets
            .replace(/['"]/g, '')              // Remove quotes
            .replace(/\s+/g, ' ')              // Normalize whitespace
            .replace(/\b(?:error|Error):\s*/g, '') // Remove "error:" prefix
            .trim();
    }
}

export default ErrorHandler;